"use client";

import { useCallback } from "react";

import { Bell, CheckCheck, LoaderCircle } from "lucide-react";
import { Virtuoso } from "react-virtuoso";

import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { cn } from "@/lib/utils";

import type { NotificationItem } from "../types/notification.types";
import { NotificationRow } from "./notification-row";

export function NotificationList({
  notifications,
  onMarkRead,
  onNotificationClick,
  onMarkAllRead,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  isNavigatingRoute = false,
  activeLoadingNotificationId,
  listHeightClassName = "h-96",
}: {
  notifications: NotificationItem[];
  onMarkRead: (id: number) => void;
  onNotificationClick?: (notification: NotificationItem) => void;
  onMarkAllRead?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  isNavigatingRoute?: boolean;
  activeLoadingNotificationId?: number;
  listHeightClassName?: string;
}) {
  const hasUnread = notifications.some((item) => !item.is_read);

  const handleEndReached = useCallback(() => {
    if (!onLoadMore || !hasMore || isLoadingMore) return;
    void onLoadMore();
  }, [hasMore, isLoadingMore, onLoadMore]);

  const renderNotificationItem = useCallback(
    (_index: number, notification: NotificationItem) => (
      <NotificationRow
        notification={notification}
        onMarkRead={onMarkRead}
        isLoading={
          isNavigatingRoute && notification.id === activeLoadingNotificationId
        }
        onClick={onNotificationClick}
        variant="compact"
      />
    ),
    [
      activeLoadingNotificationId,
      isNavigatingRoute,
      onMarkRead,
      onNotificationClick,
    ],
  );

  if (notifications.length === 0) {
    return (
      <Empty className="border-none px-6 py-10">
        <EmptyMedia variant="icon">
          <Bell />
        </EmptyMedia>
        <EmptyHeader>
          <EmptyTitle>No notifications yet</EmptyTitle>
          <EmptyDescription>
            You&apos;re all caught up for now.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div className="bg-background sticky top-0 z-20 flex items-center justify-between gap-2 border-b px-3 py-2">
        <p className="text-sm font-semibold">Notifications</p>

        {onMarkAllRead ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onMarkAllRead}
            disabled={!hasUnread}
            className="h-8 px-2 text-xs"
          >
            <CheckCheck className="mr-1.5 size-3.5" />
            Mark all
          </Button>
        ) : null}
      </div>

      <div className={cn("h-96 min-h-0", listHeightClassName)}>
        <Virtuoso
          style={{ height: "100%" }}
          data={notifications}
          overscan={200}
          endReached={handleEndReached}
          itemContent={renderNotificationItem}
          components={{
            Footer: () =>
              isLoadingMore ? (
                <div className="text-muted-foreground flex items-center justify-center gap-2 px-3 py-4 text-sm">
                  <LoaderCircle className="size-4 animate-spin" />
                  Loading more notifications...
                </div>
              ) : hasMore ? null : (
                <div className="text-muted-foreground px-3 py-4 text-center text-sm">
                  No more notifications
                </div>
              ),
          }}
        />
      </div>
    </div>
  );
}
